import { useRouter } from 'next/router';
import React, { createContext, useContext, useMemo } from 'react';

import { getFileShareLink } from '../../utils/shareLink';

interface GetShareUrlArgs {
  domain?: string | null;
  isBulk: boolean;
}

export interface ViewerRouteValue {
  /** 공유 대상 식별자. 기존 라우트에서는 router.query.hashId */
  hashId: string;
  /** 열람 기록 id. 기존 라우트에서는 router.query.historyId */
  historyId: string;
  /** 대량발송(bulk) 공유 여부 */
  isBulk: boolean;
  /** 공유 뷰어 라우트 여부. V2 뷰어를 쓸지 가르는 값이다 */
  isShareRoute: boolean;
  /** 단축 링크(/s/{token})로 진입한 경우의 토큰 */
  shortToken?: string;
  /** 뷰어 안에서 다시 공유할 때 복사되는 URL */
  getShareUrl: (args: GetShareUrlArgs) => string;
  /** 이미 진입 기록이 있을 때 뷰어 단계로 넘어가는 방법 */
  resumeViewer: (historyId: string) => void;
}

export type ViewerRouteOverride = Partial<ViewerRouteValue>;

const ViewerRouteContext = createContext<ViewerRouteOverride | null>(null);

interface ProviderProps {
  value: ViewerRouteOverride;
  children: React.ReactNode;
}

/**
 * 뷰어가 라우트에서 추론하던 값(hashId, historyId, bulk 여부 등)을 밖에서 주입한다.
 *
 * 단축 링크 `/s/{token}` 은 주소창을 그대로 둔 채 뷰어를 렌더하므로 router에서
 * 이 값들을 읽을 수 없다. Provider가 없으면 아래 useViewerRoute가 기존 표현식
 * 그대로 폴백하므로 `/share/...` 경로와 client 앱의 미리보기 화면은 영향이 없다.
 */
export const ViewerRouteProvider = ({ value, children }: ProviderProps) => {
  return (
    <ViewerRouteContext.Provider value={value}>
      {children}
    </ViewerRouteContext.Provider>
  );
};

export const useViewerRoute = (): ViewerRouteValue => {
  const router = useRouter();
  const override = useContext(ViewerRouteContext);

  const hashId = override?.hashId ?? (router.query.hashId as string);
  const historyId = override?.historyId ?? (router.query.historyId as string);
  const isBulk = override?.isBulk ?? router.pathname.includes('bulk');
  const isShareRoute =
    override?.isShareRoute ?? router.pathname.startsWith('/share');

  return useMemo(
    () => ({
      hashId,
      historyId,
      isBulk,
      isShareRoute,
      shortToken: override?.shortToken,
      getShareUrl:
        override?.getShareUrl ??
        (({ domain, isBulk: isBulkTarget }: GetShareUrlArgs) =>
          getFileShareLink({
            hashId,
            isBulk: isBulkTarget,
            domain,
            historyId: encodeURIComponent(historyId),
          })),
      resumeViewer:
        override?.resumeViewer ??
        ((resumedHistoryId: string) => {
          router.push({
            pathname: `/share/bulk/file/redirect`,
            query: { hashId, historyId: resumedHistoryId },
          });
        }),
    }),
    [hashId, historyId, isBulk, isShareRoute, override, router],
  );
};

export default ViewerRouteContext;
